import * as React from 'react';

import { useLocation, useParams, useRoutes } from 'js/lib/useRouter';

import type {
  UniversityProgramDegreeDescriptionPageAdmissionsProcessLinks as AdmissionsProcessLinks,
  UniversityProgramDegreeDescriptionPagePathwaySectionLinks as PathwaysLinksType,
  UniversityProgramDeadlines,
} from 'bundles/degree-description/__generated__/baseContentfulTypes';
import AdmissionsProcessModule from 'bundles/degree-description/components/modules/admissions-process/AdmissionsProcessModule';
import DeadlineCard from 'bundles/degree-description/components/modules/deadline/DeadlineCard';
import DeadlineModule from 'bundles/degree-description/components/modules/deadline/DeadlineModule';
import EoiInlineModule from 'bundles/degree-description/components/modules/expression-of-interest/EoiInlineModule';
import AtAGlanceHorizontalBlock from 'bundles/degree-description/components/modules/hero/AtAGlanceHorizontalBlock';
import DeadlinesBlock from 'bundles/degree-description/components/modules/hero/DeadlinesBlock';
import EoiButtonsBlock from 'bundles/degree-description/components/modules/hero/EoiButtonsBlock';
import HeroBanner from 'bundles/degree-description/components/modules/hero/HeroBanner';
import HeroModule from 'bundles/degree-description/components/modules/hero/HeroModule';
import InformationalSectionContainer from 'bundles/degree-description/components/modules/hero/InformationalSectionContainer';
import PathwaysModule from 'bundles/degree-description/components/modules/pathways/PathwaysModule';
import PersonalizedEmbeddedMsg from 'bundles/degree-description/components/modules/personalization/PersonalizedEmbeddedMsg';
import SchemaMarkup from 'bundles/degree-description/components/modules/structured-data-markup/SchemaMarkup';
import { AppFeeWaiver } from 'bundles/degree-description/components/shared/AppFeeWaiver';
import { HideOnDesktop, HideOnMobile } from 'bundles/degree-description/components/shared/BreakpointDisplay';
import ModuleContainerStyleWrap from 'bundles/degree-description/components/shared/ModuleContainerStyleWrap';
import InlineNavigation from 'bundles/degree-description/components/shared/Navigation/InlineNavigation';
import RichText from 'bundles/degree-description/components/shared/RichText';
import type { PropsFromDegreePageContent } from 'bundles/degree-description/pages/consumer/degree-description-page/DegreePageContent';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import { showAppFeeWaiver } from 'bundles/degree-description/utils/appFeeWaiver';
import { getMessagesToDisplay } from 'bundles/degree-description/utils/personalizedMessageCriteria';
import { PAGE_NAMES_MARKUP } from 'bundles/degree-description/utils/schema-markup';
import { getNavigationItems } from 'bundles/degree-description/utils/utils';

type Props = PropsFromDegreePageContent;

const AdmissionsPageContent = ({
  pageContent,
  eoiContainerText,
  eoiButtons,
  setEoiTrackingIndex,
  personalizedDdpData,
  hasAppFeeWaiverQualification,
  averageCourseStarRating,
}: Props) => {
  const {
    admissionsAbout,
    atAGlance,
    backgroundImage,
    partner,
    programName,
    deadlineSectionCollection,
    pathwaySection,
    admissionsProcess,
  } = pageContent;

  // Page Section Tracking
  const { useGetViewPageSectionRef } = useDegreeDescriptionEventingContext();
  const pageHeaderTrackingRef = useGetViewPageSectionRef('page_header');
  const pageBodyTrackingRef = useGetViewPageSectionRef('page_body');

  const location = useLocation();
  const { slug } = useParams();
  const routes = useRoutes();

  // Personalization
  const messagesToDisplay = getMessagesToDisplay(
    personalizedDdpData,
    slug,
    programName,
    partner?.partnerName,
    deadlineSectionCollection?.items as UniversityProgramDeadlines[]
  );

  return (
    <div>
      <SchemaMarkup pageName={PAGE_NAMES_MARKUP.admissions} pathName={location.pathname} pageContent={pageContent} />
      <HeroModule
        informationalSection={
          <InformationalSectionContainer
            content={
              <>
                {admissionsAbout?.json && (
                  <div ref={pageHeaderTrackingRef}>
                    <RichText
                      content={admissionsAbout?.json}
                      contentLinks={admissionsAbout?.links}
                      linkTrackingName="admissionsAbout_body_link"
                      buttonTrackingNameV3="degree_info_link"
                      trackingSection="page_header"
                    />
                  </div>
                )}
                {deadlineSectionCollection?.items && deadlineSectionCollection?.items.length > 0 && (
                  <DeadlineModule
                    deadlineItems={deadlineSectionCollection.items.map((deadline) => (
                      <DeadlineCard
                        key={`degree-deadline-card-${deadline?.sys?.id}`}
                        title={deadline?.title}
                        enrollApplyStart={deadline?.enrollApplyStart}
                        enrollApplyEnd={deadline?.enrollApplyEnd}
                        coursesStart={deadline?.coursesStart}
                        deadlineType={deadline?.deadlineType}
                        enrollApplyCustomText={deadline?.enrollApplyCustomText}
                        coursesStartCustomText={deadline?.coursesStartCustomText}
                      />
                    ))}
                  />
                )}
              </>
            }
            sidebarContent={
              <HideOnMobile>
                <>
                  {/*
                   // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess */}
                  {showAppFeeWaiver(slug, hasAppFeeWaiverQualification) && <AppFeeWaiver slug={slug} />}
                  <DeadlinesBlock finalDeadlineInfo={atAGlance?.finalDeadlineInfo} />
                </>
              </HideOnMobile>
            }
          />
        }
        heroBanner={
          <HeroBanner
            backgroundImageUrl={backgroundImage?.url ?? ''}
            partnerLogoUrl={partner?.partnerLogo?.url ?? ''}
            partnerName={partner?.partnerName ?? ''}
            programName={programName ?? ''}
            averageCourseStarRating={averageCourseStarRating}
            sidebarContent={
              <EoiButtonsBlock
                eoiContainerText={eoiContainerText}
                eoiButtons={eoiButtons}
                trackingIndex={1}
                setEoiTrackingIndex={setEoiTrackingIndex}
              />
            }
          />
        }
        contentInBetween={
          <>
            <AtAGlanceHorizontalBlock atAGlance={atAGlance} />
            <InlineNavigation
              navigationItems={getNavigationItems()}
              // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              parentNavRoute={routes[routes.length - 2].name}
              // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              currentNavRoute={routes[routes.length - 1].name}
              // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              slug={slug}
            />
          </>
        }
      />
      <div ref={pageBodyTrackingRef}>
        <HideOnDesktop>
          <>
            {messagesToDisplay.embeddedMessageToDisplay && (
              <ModuleContainerStyleWrap blueBackground>
                <PersonalizedEmbeddedMsg
                  personalizedMessage={messagesToDisplay.embeddedMessageToDisplay}
                  eoiButtons={eoiButtons}
                  setEoiTrackingIndex={setEoiTrackingIndex}
                />
              </ModuleContainerStyleWrap>
            )}
            {/*
             // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess */}
            {showAppFeeWaiver(slug, hasAppFeeWaiverQualification) && <AppFeeWaiver slug={slug} />}
            <DeadlinesBlock finalDeadlineInfo={atAGlance?.finalDeadlineInfo} />
          </>
        </HideOnDesktop>
        <HideOnMobile>
          <>
            {messagesToDisplay.embeddedMessageToDisplay && (
              <ModuleContainerStyleWrap blueBackground>
                <PersonalizedEmbeddedMsg
                  personalizedMessage={messagesToDisplay.embeddedMessageToDisplay}
                  eoiButtons={eoiButtons}
                  setEoiTrackingIndex={setEoiTrackingIndex}
                />
              </ModuleContainerStyleWrap>
            )}
          </>
        </HideOnMobile>

        {pathwaySection && (
          <ModuleContainerStyleWrap>
            <PathwaysModule
              pathwaysText={pathwaySection?.json}
              pathwayBlocks={pathwaySection?.links as PathwaysLinksType}
            />
          </ModuleContainerStyleWrap>
        )}

        {admissionsProcess && (
          <ModuleContainerStyleWrap>
            <AdmissionsProcessModule
              content={admissionsProcess.json}
              links={admissionsProcess.links as AdmissionsProcessLinks}
            />
          </ModuleContainerStyleWrap>
        )}

        <HideOnMobile>
          <EoiInlineModule
            eoiContainerText={eoiContainerText}
            eoiButtons={eoiButtons}
            trackingIndex={2}
            setEoiTrackingIndex={setEoiTrackingIndex}
          />
        </HideOnMobile>
      </div>
    </div>
  );
};

export default AdmissionsPageContent;
